/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/dvrdPassword.scss';

import React, {useState} from 'react';
import DvrdInputController, {InputControllerProps} from "./dvrdInputController";
import classNames from 'classnames';
import {ElementPosition, StrengthGauge} from "../util/interfaces";
import AwesomeIcon from "../icon/awesomeIcon";

interface Props extends Omit<InputControllerProps, 'ornaments' | 'ornamentClassName'> {
    withPasswordStrength?: boolean;
    passwordStrengthGauges?: Array<StrengthGauge>;
    wrapperClassName?: string;
    passwordGaugeClassName?: string;
}

const defaultStrengthGauges = (): StrengthGauge[] => [
    {validator: (password: string) => password.length >= 8},
    {validator: (password: string) => /\d+/.test(password)},
    {validator: (password: string) => /[A-Z]+/.test(password)},
    {validator: (password: string) => /[!@#$%^&*()_+=\-\[\]{}|'";:/?.>,<`~]/.test(password)}
];

export default function DVRDPasswordInput(props: Props) {
    const [inputType, setInputType] = useState<'password' | 'text'>('password');

    function getPasswordStrength(): number {
        const gauges = props.passwordStrengthGauges ?? defaultStrengthGauges();
        return gauges.reduce(
            (prev: number, current: StrengthGauge) => current.validator(props.value.toString()) ? prev + 1 : prev, 0);
    }

    function onClickOrnament() {
        setInputType(inputType === 'password' ? 'text' : 'password');
    }

    function renderOrnament() {
        const iconName = inputType === 'password' ? 'eye' : 'eye-slash';
        console.debug(iconName);
        return {
            placement: ElementPosition.RIGHT,
            element: <AwesomeIcon name={iconName} onClick={onClickOrnament} className='dvrd-password-toggle'/>
        };
    }

    function renderStrength() {
        if (!props.withPasswordStrength) return null;
        const strength = getPasswordStrength();
        return (
            <div className={classNames('password-meter', props.passwordGaugeClassName)}>
                <span className={classNames('password-strength', strength > 0 && 'pass')}/>
                <span className={classNames('password-strength', strength > 1 && 'pass')}/>
                <span className={classNames('password-strength', strength > 2 && 'pass')}/>
                <span className={classNames('password-strength', strength > 3 && 'pass')}/>
            </div>
        );
    }

    return (
        <div className={classNames('dvrd-password-input', props.wrapperClassName)}>
            <DvrdInputController {...props} inputProps={{...props.inputProps, type: inputType}}
                                 ornaments={renderOrnament()}/>
            {renderStrength()}
        </div>
    );
}

DVRDPasswordInput.displayName = 'DVRDPasswordInput';